import { CountryCode } from '@carsys/enums/country-code'
import { FunctionType } from '@cbgms/api/modules/enums'
import { formatters } from '@cbgms/base/utils/datetime'
import { enumToOptions } from '@cbgms/base/utils/helpers'
import { isRequired, isValidEmail } from '@cbgms/base/utils/validations'
import { ReduxDateTimePicker as DateTimePicker } from '@cbgms/components/form/DateTimePickerBasic'
import Dropdown from '@cbgms/components/form/Dropdown'
import Input from '@cbgms/components/form/Input'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
import { DropdownItemProps, Form, Header } from 'semantic-ui-react'
import { IRootReducerState } from 'state/reducers'

interface IOwnProps {
  form: string
  disableOutOfService?: boolean
}

interface IMapStateToProps {
  employeeSkillOptions: DropdownItemProps[]
  visitorCountry: CountryCode
  isMOTDisabled: boolean
}

interface IMapDispatchToProps {
  change: typeof change
}

export interface GeneralProps extends IOwnProps, IMapStateToProps, IMapDispatchToProps {}

export const mapStateToProps = (state: IRootReducerState, props: IOwnProps): IMapStateToProps => {
  const functions = formValueSelector(props.form)(state, 'Functions')

  return {
    employeeSkillOptions: state.employee.skills,
    visitorCountry: state.shop.VisitorCountry,
    isMOTDisabled: functions ? !functions.includes(FunctionType.MotInspector) : true
  }
}

const enhance = connect<IMapStateToProps, IMapDispatchToProps, IOwnProps, IRootReducerState>(mapStateToProps, { change })

export const General: React.FC<GeneralProps> = props => {
  const { t } = useTranslation()
  const { employeeSkillOptions, disableOutOfService = false } = props

  const functionOptions = enumToOptions(FunctionType, 'EnumPosition')

  return (
    <React.Fragment>
      <Header as='h2'>{t('setting_employee.general')}</Header>
      <Form.Group widths='equal'>
        <Input fluid required validate={isRequired} label={t('Account.Name')} name='Name' maxLength='50' data-testid='name' />
        <Input fluid label={t('Account.NamePrefix')} name='NamePrefix' maxLength='25' />
        <Input
          fluid
          label={t('Account.FirstName')}
          name='FirstName'
          maxLength='25'
          data-testid='firstname'
          required
          validate={isRequired}
        />
      </Form.Group>
      <Form.Group widths='3'>
        <Input fluid label={t('Account.Initials')} name='Initials' maxLength='15' />
      </Form.Group>
      <Form.Group widths='equal'>
        <Input
          label={t('setting_employee.emailaddresswork')}
          maxLength='255'
          name='EmailAddressWork'
          data-testid='email-work'
          validate={isValidEmail}
        />
        <DateTimePicker
          formatters={formatters.date}
          label={t('setting_employee.date_in_service')}
          name='DateInService'
          timeFormat={false}
          viewMode='years'
          data-testid='date-in-service'
        />
        <DateTimePicker
          disabled={disableOutOfService}
          formatters={formatters.date}
          label={t('setting_employee.date_out_of_service')}
          name='DateOutOfService'
          timeFormat={false}
          viewMode='years'
          data-testid='employee-general-outofservice'
        />
      </Form.Group>
      <Form.Group widths='equal'>
        <Dropdown label={t('setting_employee.functions')} multiple name='Functions' options={functionOptions} selection />
        <Dropdown label={t('setting_employee.skills')} multiple name='Skills' options={employeeSkillOptions} selection />
      </Form.Group>
    </React.Fragment>
  )
}

export default enhance(General)
